import { useState } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { Link, useNavigate } from "react-router-dom"
import { FolderKanban, Search, ExternalLink, FileText, Plus, Building2 } from "lucide-react"
import { useTenantFilter } from "@/hooks/useTenantFilter"
import { FilterBar } from "@/components/portal/FilterBar"
import { CreateContactModal } from "@/components/portal/CreateContactModal"
import { ObjektEditModal } from "@/components/portal/ObjektEditModal"
import { toast } from "@/components/portal/Toast"

// HERO liefert link_view relativ ("/partner/Documents/view/...") — wir
// prefixen mit der HERO-Web-Base damit der Link funktioniert.
const HERO_WEB_BASE = "https://login.hero-software.de"
function heroLink(linkView: string | null): string | null {
  if (!linkView) return null
  if (linkView.startsWith("http://") || linkView.startsWith("https://")) return linkView
  if (linkView.startsWith("/")) return HERO_WEB_BASE + linkView
  return `${HERO_WEB_BASE}/${linkView}`
}

/**
 * /projekte — Projekt-Liste mit Volltext-Suche + Dokument-Suche.
 *
 * Phase A (11.05.2026): read-only, sucht live in HERO ueber 2 Endpoints:
 *  - /api/v1/projects/search?q=  (Projekte: Name, Adresse, Kontakt)
 *  - /api/v1/projects/documents/search?q=  (Angebote/Rechnungen per Nr.)
 *
 * Detail-Klick → /projekte/:id
 * Dokument-Klick → Projekt-Detail des verknuepften Projekts (oder externer
 * HERO-Link wenn kein Projekt verbunden).
 */

type ProjectSearchItem = {
  id: number
  name: string
  code: string | null
  contact_name: string
  status: string
}
type DocSearchItem = {
  id: number; nr: string; type: string; type_name: string
  created: string | null; link_view: string | null; status_name: string
  project: { id: number; name: string | null; project_nr: string | null } | null
  contact_name: string | null
}

type ObjektItem = {
  id: number
  number: number | null
  name: string
  street: string | null
  zip: string | null
  city: string | null
  customer_id: number
  kunde_name: string | null
  kunde_number: number | null
  bunchofkeys: string | null
  description: string | null
  planning_info: string | null
}

export default function ProjectsList() {
  const [tenantSlug, setTenantSlug] = useTenantFilter()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [q, setQ] = useState("")
  const [createOpen, setCreateOpen] = useState(false)
  const queryEnabled = q.trim().length >= 2

  // Backend-Provider des Tenants → fortytools zeigt eine Objekt-Liste statt der
  // reinen HERO-Suche.
  const tenantInfo = useQuery<{ backend_provider?: string } | null>({
    queryKey: ["tenant-provider", tenantSlug || ""],
    queryFn: async () => {
      if (!tenantSlug) return null
      const res = await fetch(`/api/v1/tenants/${encodeURIComponent(tenantSlug)}`, { credentials: "include" })
      if (!res.ok) return null
      return res.json()
    },
    enabled: !!tenantSlug,
  })
  const isForty = (tenantInfo.data?.backend_provider || "hero") === "fortytools"

  // fortytools-Objekte (GET /projects). Client-seitig nach Name filterbar.
  const objekte = useQuery<{ items: ObjektItem[]; total: number }>({
    queryKey: ["objekte", tenantSlug],
    enabled: isForty && !!tenantSlug,
    queryFn: async () => {
      const res = await fetch(`/api/v1/projects/objekte?tenant=${encodeURIComponent(tenantSlug)}`, { credentials: "include" })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
  })
  const [editObjekt, setEditObjekt] = useState<ObjektItem | null>(null)
  const objekteFiltered = (objekte.data?.items || []).filter((o) => {
    const needle = q.trim().toLowerCase()
    if (!needle) return true
    return o.name.toLowerCase().includes(needle)
      || (o.kunde_name || "").toLowerCase().includes(needle)
      || String(o.number ?? "").includes(needle)
      || String(o.kunde_number ?? "").includes(needle)
  })

  const projects = useQuery<{ items: ProjectSearchItem[]; total: number }>({
    queryKey: ["projects-search", q, tenantSlug],
    enabled: queryEnabled,
    queryFn: async () => {
      const url = `/api/v1/projects/search?q=${encodeURIComponent(q.trim())}${tenantSlug ? `&tenant=${encodeURIComponent(tenantSlug)}` : ""}`
      const r = await fetch(url, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
  })

  const docs = useQuery<{ items: DocSearchItem[]; total: number }>({
    queryKey: ["documents-search", q, tenantSlug],
    enabled: queryEnabled,
    queryFn: async () => {
      const url = `/api/v1/projects/documents/search?q=${encodeURIComponent(q.trim())}${tenantSlug ? `&tenant=${encodeURIComponent(tenantSlug)}` : ""}`
      const r = await fetch(url, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
  })

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
        {isForty ? <Building2 className="w-6 h-6" style={{ color: "#475569" }} />
                 : <FolderKanban className="w-6 h-6" style={{ color: "#475569" }} />}
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>
          {isForty ? "Objekte" : "Projekte"}
          {isForty && objekte.data ? <span style={{ fontSize: 16, fontWeight: 400, color: "#94a3b8", marginLeft: 8 }}>({objekte.data.total})</span> : null}
        </h1>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => {
            if (!tenantSlug) {
              toast("Bitte zuerst einen Tenant auswaehlen (Filter oben)", "error")
              return
            }
            setCreateOpen(true)
          }}
          style={{
            display: "inline-flex", alignItems: "center", gap: 6,
            padding: "8px 14px", background: "#1d4ed8", color: "#fff",
            border: "none", borderRadius: 6, fontSize: 13, fontWeight: 600,
            cursor: "pointer",
          }}
        >
          <Plus className="w-4 h-4" />
          {isForty ? "Neues Objekt" : "Neues Projekt"}
        </button>
      </div>

      {/* Tenant-Auswahl — vorher gar nicht vorhanden auf dieser Seite */}
      <div style={{ marginBottom: 16 }}>
        <FilterBar tenant={tenantSlug} onTenantChange={setTenantSlug} />
      </div>

      {/* Neues-Projekt/Objekt-Modal — reuse von CreateContactModal mit mode=calendar */}
      <CreateContactModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        mode="calendar"
        tenantSlug={tenantSlug || ""}
        itemId={0}
        initialWithProject={true}
        onSuccess={(result) => {
          const pid = result?.project_id || 0
          if (isForty) {
            toast("Objekt angelegt")
            qc.invalidateQueries({ queryKey: ["objekte", tenantSlug] })
          } else if (pid > 0) {
            // #98: keine interne Nummer im Toast — die Seite des Vorgangs
            // oeffnet sich direkt danach und zeigt Kuerzel und Titel.
            toast("Projekt angelegt")
            navigate(`/projekte/${pid}`)
          } else {
            toast("Projekt angelegt")
          }
        }}
      />

      {/* Objekt bearbeiten (fortytools) */}
      <ObjektEditModal
        open={!!editObjekt}
        onClose={() => setEditObjekt(null)}
        tenantSlug={tenantSlug || ""}
        objekt={editObjekt}
      />

      {/* Suchleiste */}
      <div style={{
        position: "relative", marginBottom: 20,
        background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb",
      }}>
        <Search className="w-4 h-4" style={{
          position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
          color: "#94a3b8", pointerEvents: "none",
        }} />
        <input
          type="text"
          placeholder={isForty
            ? "Objekt-Name filtern…"
            : "Suchen: Projekt-Name, Kontakt-Name, Adresse, ANG-Nr., Rechnungs-Nr…"}
          value={q}
          onChange={(e) => setQ(e.target.value)}
          autoFocus
          style={{
            width: "100%", padding: "14px 14px 14px 42px",
            border: "none", outline: "none", borderRadius: 10, fontSize: 15,
            background: "transparent",
          }}
        />
      </div>

      {/* fortytools: Objekt-Liste (kein 2-Zeichen-Zwang, Suchfeld = Namensfilter) */}
      {isForty && (
        <div>
          {objekte.isLoading && <div style={{ color: "#94a3b8", fontSize: 13, padding: 20 }}>Lädt Objekte…</div>}
          {objekte.isError && <div style={{ color: "#dc2626", fontSize: 13, padding: 20 }}>Fehler beim Laden der Objekte</div>}
          {objekte.data && objekteFiltered.length === 0 && (
            <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 14 }}>
              {objekte.data.total === 0 ? "Noch keine Objekte für diesen Tenant." : `Kein Objekt passt zu „${q}".`}
            </div>
          )}
          {objekteFiltered.length > 0 && (
            <div className="pp-card" style={{ padding: 0 }}>
              <div className="pp-tbl-wrap" style={{ border: "none", boxShadow: "none", margin: 0 }}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 90 }}>Nummer</th>
                      <th>Objektname / Adresse</th>
                      <th className="col-hide-md">Kunde / Kundennummer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {objekteFiltered.map((o) => {
                      const addr = [o.street, [o.zip, o.city].filter(Boolean).join(" ")].filter(Boolean).join(" · ")
                      return (
                        <tr
                          key={o.id}
                          style={{ cursor: "pointer" }}
                          title="Klicken zum Bearbeiten"
                          onClick={() => setEditObjekt(o)}
                        >
                          <td style={{ fontWeight: 500, color: "var(--accent-color)" }}>{o.number ?? o.id}</td>
                          <td>
                            <div style={{ fontWeight: 600 }}>{o.name}</div>
                            {addr && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{addr}</div>}
                          </td>
                          <td className="col-hide-md">
                            <div>{o.kunde_name || "—"}</div>
                            {o.kunde_number != null && <div style={{ fontSize: 12, color: "var(--text-muted)" }}>{o.kunde_number}</div>}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {!isForty && !queryEnabled && (
        <div style={{ padding: 40, textAlign: "center", color: "#94a3b8", fontSize: 14 }}>
          Tippe mindestens 2 Zeichen um zu suchen.
        </div>
      )}

      {!isForty && queryEnabled && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>
          {/* Projekte */}
          <div>
            <h2 style={{
              fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5,
              color: "#64748b", marginBottom: 10, fontWeight: 700,
            }}>
              Projekte {projects.data ? `(${projects.data.total})` : ""}
            </h2>
            {projects.isLoading && <div style={{ color: "#94a3b8", fontSize: 13 }}>Sucht…</div>}
            {projects.isError && <div style={{ color: "#dc2626", fontSize: 13 }}>Fehler beim Suchen</div>}
            {projects.data?.items.length === 0 && (
              <div style={{ color: "#94a3b8", fontSize: 13 }}>Keine Projekte gefunden</div>
            )}
            {projects.data?.items.map((p) => (
              <Link
                key={p.id}
                to={`/projekte/${p.id}`}
                style={{
                  display: "block", padding: "12px 14px", marginBottom: 6,
                  background: "#fff", borderRadius: 8, border: "1px solid #e5e7eb",
                  textDecoration: "none", color: "inherit",
                  transition: "border-color 0.15s",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = "#94a3b8")}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#e5e7eb")}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "baseline" }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: "#1a1a2e" }}>
                      {p.code && <span style={{ color: "#475569", marginRight: 6 }}>{p.code}</span>}
                      {p.name}
                    </div>
                    {p.contact_name && (
                      <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                        {p.contact_name}
                      </div>
                    )}
                  </div>
                  {p.status && (
                    <span style={{
                      fontSize: 12, color: "#64748b",
                      padding: "2px 8px", background: "#f1f5f9", borderRadius: 10,
                      flexShrink: 0,
                    }}>{p.status}</span>
                  )}
                </div>
              </Link>
            ))}
          </div>

          {/* Dokumente */}
          <div>
            <h2 style={{
              fontSize: 12, textTransform: "uppercase", letterSpacing: 0.5,
              color: "#64748b", marginBottom: 10, fontWeight: 700,
            }}>
              Dokumente {docs.data ? `(${docs.data.total})` : ""}
            </h2>
            {docs.isLoading && <div style={{ color: "#94a3b8", fontSize: 13 }}>Sucht…</div>}
            {docs.isError && <div style={{ color: "#dc2626", fontSize: 13 }}>Fehler beim Suchen</div>}
            {docs.data?.items.length === 0 && (
              <div style={{ color: "#94a3b8", fontSize: 13 }}>Keine Dokumente gefunden</div>
            )}
            {docs.data?.items.map((d) => (
              <div
                key={d.id}
                style={{
                  padding: "12px 14px", marginBottom: 6,
                  background: "#fff", borderRadius: 8, border: "1px solid #e5e7eb",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <FileText className="w-3.5 h-3.5" style={{ color: "#475569" }} />
                      <span style={{ fontWeight: 600, fontSize: 14, color: "#1a1a2e" }}>
                        {d.nr || `#${d.id}`}
                      </span>
                      <span style={{ fontSize: 12, color: "#64748b" }}>
                        {d.type_name}
                      </span>
                    </div>
                    {(d.project || d.contact_name) && (
                      <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                        {d.project && (
                          <Link to={`/projekte/${d.project.id}`} style={{ color: "#1d4ed8", textDecoration: "none" }}>
                            {d.project.project_nr ? `${d.project.project_nr} · ` : ""}{d.project.name}
                          </Link>
                        )}
                        {d.project && d.contact_name && " · "}
                        {d.contact_name}
                      </div>
                    )}
                    {d.status_name && (
                      <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>
                        Status: {d.status_name}
                      </div>
                    )}
                  </div>
                  {heroLink(d.link_view) && (
                    <a
                      href={heroLink(d.link_view) || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Oeffnet in HERO (neuer Tab)"
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 4,
                        fontSize: 12, color: "#1d4ed8", textDecoration: "none",
                        flexShrink: 0, alignSelf: "flex-start",
                      }}
                    >
                      In HERO öffnen <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
